/**
 * The Payouts page (CONTEXT.md "Payouts"), the pure part: what each Account's
 * row says, which request a row can make, the merged 30-day history and who
 * asked for each payout (Requested by, Unconfirmed payout request).
 *
 * Money is cents in the Account's currency: OnlyFans answers in dollars,
 * Fansly in thousandths of a dollar.
 */
import { minPayout, plainText, type Balances, type Receive } from '../of/statements/statementsData'
import type { PayoutMethod, PayoutRequest } from '../fansly/wallet/walletData'

export type Platform = 'onlyfans' | 'fansly'

export const platformOf = (p: string | null | undefined): Platform => (p === 'fansly' ? 'fansly' : 'onlyfans')

export const HISTORY_DAYS = 30
/** An unconfirmed attempt matches a platform request made up to this long after it. */
export const MATCH_WINDOW_MS = 10 * 60 * 1000
/** …or this long before it (clocks differ). */
export const MATCH_SKEW_MS = 60 * 1000

export const centsFromDollars = (v: unknown) => {
  const n = Number(v)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}
export const centsFromMills = (v: unknown) => {
  const n = Number(v)
  return Number.isFinite(n) ? Math.round(n / 10) : 0
}

// ── Rows ───────────────────────────────────────────────────────────────────

/** Why a row cannot request a payout right now (null: it can). */
export type Blocker =
  /** OnlyFans pays this Account out on a schedule (weekly / monthly), not on request. */
  | 'automatic'
  | 'cannotReceive'
  | 'belowMin'
  | 'empty'
  | 'noMethod'
  | 'blocked'
  | 'taxLocked'

export interface OnlyFansDetail {
  platform: 'onlyfans'
  balances: Balances
  receive: Receive | null
}

export interface FanslyDetail {
  platform: 'fansly'
  /** Available balance as Fansly keeps it (thousandths), for its Request Payout dialog. */
  availableMills: number
  blocked: boolean
  methods: PayoutMethod[]
}

export interface RowData {
  available: number | null
  pending: number | null
  currency: string
  detail: OnlyFansDetail | FanslyDetail
}

export type RowState =
  | { status: 'loading' }
  | { status: 'ready'; data: RowData }
  | { status: 'error'; message: string }
  | { status: 'notConnected' }

export function onlyFansRow(balances: Balances, receive: Receive | null): RowData {
  const available = Number(balances.payoutAvailable)
  const pending = Number(balances.payoutPending)
  return {
    available: Number.isFinite(available) ? centsFromDollars(available) : null,
    pending: Number.isFinite(pending) ? centsFromDollars(pending) : null,
    currency: balances.currency || 'USD',
    detail: { platform: 'onlyfans', balances, receive },
  }
}

export function fanslyRow(availableMills: number | null, pendingMills: number | null, blocked: boolean, methods: PayoutMethod[]): RowData {
  return {
    available: availableMills === null ? null : centsFromMills(availableMills),
    pending: pendingMills === null ? null : centsFromMills(pendingMills),
    currency: 'USD',
    detail: { platform: 'fansly', availableMills: availableMills ?? 0, blocked, methods },
  }
}

/** The methods Fansly's Request Payout dialog offers. */
export const usableMethods = (methods: PayoutMethod[]) => methods.filter((m) => m.status === 'approved' || m.status === 'idle' || m.status === 'requested')

/** What stops this row's Request payout, as each platform's own page decides it. */
export function blockerOf(data: RowData): Blocker | null {
  const d = data.detail
  if (d.platform === 'onlyfans') {
    const period = String(d.balances.withdrawalPeriod ?? '')
    if (period && period !== 'manual') return 'automatic'
    if (d.receive?.canReceiveManualPayout === false) return 'cannotReceive'
    if ((Number(d.balances.payoutAvailable) || 0) < minPayout(d.balances)) return (Number(d.balances.payoutAvailable) || 0) > 0 ? 'belowMin' : 'empty'
    return null
  }
  if (d.blocked) return 'blocked'
  const usable = usableMethods(d.methods)
  if (!usable.length) return 'noMethod'
  if (usable.every((m) => m.taxLocked)) return 'taxLocked'
  if (d.availableMills <= 0) return 'empty'
  return null
}

export interface RowAccount {
  account_id: string
  name: string
  username: string
  avatar_url: string
  platform: Platform
}

const rank = (s: RowState) => (s.status === 'notConnected' ? 2 : s.status === 'error' ? 1 : 0)
const availableOf = (s: RowState) => (s.status === 'ready' ? s.data.available ?? -1 : -1)

/** Highest available first; failed rows below, not-connected ones last. */
export function sortRows<T extends { account: RowAccount; state: RowState }>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) => rank(a.state) - rank(b.state) || availableOf(b.state) - availableOf(a.state) || a.account.name.localeCompare(b.account.name),
  )
}

export interface Totals {
  available: number
  pending: number
  /** Accounts whose balance is in the sums. */
  counted: number
  notConnected: number
  failed: number
  loading: number
}

export function totalsOf(states: RowState[]): Totals {
  const t: Totals = { available: 0, pending: 0, counted: 0, notConnected: 0, failed: 0, loading: 0 }
  for (const s of states) {
    if (s.status === 'ready') {
      t.available += s.data.available ?? 0
      t.pending += s.data.pending ?? 0
      t.counted += 1
    } else if (s.status === 'notConnected') t.notConnected += 1
    else if (s.status === 'error') t.failed += 1
    else t.loading += 1
  }
  return t
}

export function matchesSearch(account: RowAccount, query: string): boolean {
  const q = query.trim().toLowerCase().replace(/^@/, '')
  if (!q) return true
  return account.name.toLowerCase().includes(q) || account.username.toLowerCase().includes(q)
}

// ── History ────────────────────────────────────────────────────────────────

export type RequestStatus = 'processing' | 'done' | 'rejected' | 'canceled' | 'unconfirmed'

/** One payout request as the platform reports it. */
export interface PlatformRequest {
  id: string
  accountId: string
  platform: Platform
  createdAt: number
  amount: number
  currency: string
  status: Exclude<RequestStatus, 'unconfirmed'>
  rejectReason: string
  methodId: string
}

/** A payout request Onlymind sent (GET /v1/payout-hub/attempts). */
export interface Attempt {
  id: string
  account_id: string
  platform: Platform
  user_id: string | null
  username: string | null
  amount_cents: number
  currency: string
  payout_method_id: string | null
  outcome: 'ok' | 'unknown'
  platform_request_id: string | null
  created_at: string
}

const toMs = (v: unknown) => {
  const ms = typeof v === 'number' ? (v < 1e12 ? v * 1000 : v) : Date.parse(String(v ?? ''))
  return Number.isFinite(ms) ? ms : 0
}

/** A row of OnlyFans' /v1/payouts/requests. */
export function onlyFansRequest(accountId: string, row: Record<string, unknown>): PlatformRequest {
  const st = String(row.state ?? '').toLowerCase()
  return {
    id: String(row.id ?? ''),
    accountId,
    platform: 'onlyfans',
    createdAt: toMs(row.createdAt),
    amount: centsFromDollars(row.amount),
    currency: String(row.currency || 'USD'),
    status: st === 'approved' ? 'done' : st === 'rejected' ? 'rejected' : st === 'canceled' || st === 'cancelled' ? 'canceled' : 'processing',
    rejectReason: st === 'rejected' ? plainText(row.rejectReason) : '',
    methodId: '',
  }
}

/** Fansly's PAYOUT_REQUEST_STATUS on our four. */
const FANSLY_STATUS: Record<string, PlatformRequest['status']> = {
  created: 'processing',
  requested: 'processing',
  approved: 'processing',
  processing: 'processing',
  processed: 'done',
  canceled: 'canceled',
  refunded: 'rejected',
  error: 'rejected',
}

export function fanslyRequest(accountId: string, r: PayoutRequest): PlatformRequest {
  return {
    id: r.id,
    accountId,
    platform: 'fansly',
    createdAt: r.createdAt,
    amount: centsFromMills(r.amount),
    currency: 'USD',
    status: FANSLY_STATUS[r.status] ?? 'processing',
    rejectReason: '',
    methodId: r.methodId,
  }
}

export interface HistoryEntry {
  key: string
  accountId: string
  platform: Platform
  createdAt: number
  amount: number
  currency: string
  status: RequestStatus
  rejectReason: string
  methodId: string
  /** The Onlymind person who asked; null = made outside Onlymind. */
  requestedBy: string | null
}

const sameAccountAmount = (a: Attempt, r: PlatformRequest) => a.account_id === r.accountId && a.amount_cents === r.amount
const inWindow = (attemptAt: number, requestAt: number) => requestAt >= attemptAt - MATCH_SKEW_MS && requestAt <= attemptAt + MATCH_WINDOW_MS

/**
 * The platform's requests of the last 30 days, each with who asked, plus the
 * attempts whose outcome Onlymind never learned and no platform request
 * accounts for yet (unconfirmed), newest first.
 */
export function buildHistory(requests: PlatformRequest[], attempts: Attempt[], since: number): HistoryEntry[] {
  const byId = new Map<string, Attempt>()
  for (const a of attempts) if (a.outcome === 'ok' && a.platform_request_id) byId.set(`${a.account_id}:${a.platform_request_id}`, a)

  const recent = requests.filter((r) => r.createdAt >= since)
  const claimed = new Map<string, string | null>()
  for (const r of recent) {
    const a = byId.get(`${r.accountId}:${r.id}`)
    if (a) claimed.set(`${r.accountId}:${r.id}`, a.username || a.user_id || null)
  }

  const unconfirmed: HistoryEntry[] = []
  const unknown = attempts.filter((a) => a.outcome === 'unknown').sort((a, b) => toMs(a.created_at) - toMs(b.created_at))
  for (const a of unknown) {
    const at = toMs(a.created_at)
    // A retry of the same payout that Onlymind did hear back about settles it.
    const retried = attempts.some((o) => o.outcome === 'ok' && o.account_id === a.account_id && o.amount_cents === a.amount_cents && inWindow(at, toMs(o.created_at)))
    const match = recent
      .filter((r) => sameAccountAmount(a, r) && inWindow(at, r.createdAt) && !claimed.has(`${r.accountId}:${r.id}`))
      .sort((x, y) => x.createdAt - y.createdAt)[0]
    if (match) claimed.set(`${match.accountId}:${match.id}`, a.username || a.user_id || null)
    else if (!retried && at >= since) {
      unconfirmed.push({
        key: `attempt:${a.id}`,
        accountId: a.account_id,
        platform: a.platform,
        createdAt: at,
        amount: a.amount_cents,
        currency: a.currency || 'USD',
        status: 'unconfirmed',
        rejectReason: '',
        methodId: a.payout_method_id || '',
        requestedBy: a.username || a.user_id || null,
      })
    }
  }

  const entries: HistoryEntry[] = recent.map((r) => ({
    key: `${r.platform}:${r.accountId}:${r.id}`,
    accountId: r.accountId,
    platform: r.platform,
    createdAt: r.createdAt,
    amount: r.amount,
    currency: r.currency,
    status: r.status,
    rejectReason: r.rejectReason,
    methodId: r.methodId,
    requestedBy: claimed.get(`${r.accountId}:${r.id}`) ?? null,
  }))
  return [...entries, ...unconfirmed].sort((a, b) => b.createdAt - a.createdAt)
}

/** The Account's newest unconfirmed request, which a new request must first warn about. */
export function openUnconfirmed(history: HistoryEntry[], accountId: string): HistoryEntry | null {
  return history.find((h) => h.accountId === accountId && h.status === 'unconfirmed') ?? null
}

export type StatusFilter = 'all' | RequestStatus

export function filterHistory(history: HistoryEntry[], status: StatusFilter, accountId: string): HistoryEntry[] {
  return history.filter((h) => (status === 'all' || h.status === status) && (!accountId || h.accountId === accountId))
}

// ── Formatting ─────────────────────────────────────────────────────────────

export function formatCents(cents: number | null, currency = 'USD', lang = 'en'): string {
  if (cents === null) return '—'
  try {
    return new Intl.NumberFormat(lang, { style: 'currency', currency: currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100)
  } catch {
    return `$${(cents / 100).toFixed(2)}`
  }
}
